import React from "react";
import * as maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import "../../lib/maplibre-worker";
import type { ActivityShareData } from "@atlas/shared";
import { MAP_COLORS } from "./map-colors";
import { createMarkerImage, LANDMARK_GLYPHS, PEAK_GLYPH, LANDMARK_MARKER_PX, PEAK_MARKER_PX } from "./AtlasMap";

// Single-activity route map: gold trace with a soft halo, the map's own
// collected markers for peaks and landmarks (peak-linked trig pillars collapse
// into a badged peak marker, as on the explore map) and start/finish markers. Used by the public share page
// (interactive) and by the share dialog's map-tiles capture (non-interactive,
// `preserveDrawingBuffer` so the WebGL canvas can be composited).

const MAP_STYLE = "https://tiles.openfreemap.org/styles/dark";
const { gold: GOLD } = MAP_COLORS;

export interface RoutePadding {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface RoutePinClick {
  kind: "peak" | "landmark";
  id: string;
  slug: string;
  name: string;
}

export interface ActivityRouteMapProps {
  data: ActivityShareData;
  /** fitBounds padding in CSS px (defaults to 48 all round). */
  padding?: number | RoutePadding;
  interactive?: boolean;
  preserveDrawingBuffer?: boolean;
  /** Force a device pixel ratio (captures want 1:1 image px). */
  pixelRatio?: number;
  /** Fires once the style is loaded, the route drawn and the map idle. */
  onIdle?: (map: maplibregl.Map) => void;
  /** Fires when a peak/landmark marker is clicked (interactive maps only). */
  onPinClick?: (pin: RoutePinClick) => void;
  style?: React.CSSProperties;
}

type FC = { type: "FeatureCollection"; features: unknown[] };
const EMPTY_FC: FC = { type: "FeatureCollection", features: [] };

function routeBbox(data: ActivityShareData): [number, number, number, number] | null {
  let w = Infinity,
    s = Infinity,
    e = -Infinity,
    n = -Infinity;
  const push = (lng: number, lat: number) => {
    if (lng < w) w = lng;
    if (lng > e) e = lng;
    if (lat < s) s = lat;
    if (lat > n) n = lat;
  };
  for (const c of data.activity.geojson?.coordinates ?? []) push(c[0], c[1]);
  for (const p of data.peaks) push(p.longitude, p.latitude);
  for (const l of data.landmarks) push(l.longitude, l.latitude);
  return isFinite(w) ? [w, s, e, n] : null;
}

function buildSources(data: ActivityShareData): { route: FC; pins: FC; ends: FC } {
  const coords = data.activity.geojson?.coordinates ?? [];
  const route: FC = data.activity.geojson
    ? { type: "FeatureCollection", features: [{ type: "Feature", geometry: data.activity.geojson, properties: {} }] }
    : EMPTY_FC;
  const badgedPeaks = new Set(
    data.landmarks.filter((l) => l.category === "trig_point" && l.peakId).map((l) => l.peakId as string),
  );
  const peakIds = new Set(data.peaks.map((p) => p.id));
  const pins: FC = {
    type: "FeatureCollection",
    features: [
      ...data.peaks.map((p) => ({
        type: "Feature" as const,
        geometry: { type: "Point" as const, coordinates: [p.longitude, p.latitude] },
        properties: {
          kind: "peak",
          id: p.id,
          slug: p.slug,
          name: p.name,
          icon: badgedPeaks.has(p.id) ? "peak-collected-trig" : "peak-collected",
          size: 1,
        },
      })),
      ...data.landmarks
        .filter((l) => !(l.category === "trig_point" && l.peakId && peakIds.has(l.peakId)))
        .map((l) => ({
          type: "Feature" as const,
          geometry: { type: "Point" as const, coordinates: [l.longitude, l.latitude] },
          properties: {
            kind: "landmark",
            id: l.id,
            slug: l.slug,
            name: l.name,
            icon: `lm-${l.category in LANDMARK_GLYPHS ? l.category : "trig_point"}-c`,
            size: 1,
          },
        })),
    ],
  };
  const ends: FC =
    coords.length >= 2
      ? {
          type: "FeatureCollection",
          features: [
            { type: "Feature", geometry: { type: "Point", coordinates: coords[0]! }, properties: { end: "start" } },
            {
              type: "Feature",
              geometry: { type: "Point", coordinates: coords[coords.length - 1]! },
              properties: { end: "finish" },
            },
          ],
        }
      : EMPTY_FC;
  return { route, pins, ends };
}

export function ActivityRouteMap({
  data,
  padding = 48,
  interactive = true,
  preserveDrawingBuffer = false,
  pixelRatio,
  onIdle,
  onPinClick,
  style,
}: ActivityRouteMapProps) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const mapRef = React.useRef<maplibregl.Map | null>(null);
  const onIdleRef = React.useRef(onIdle);
  onIdleRef.current = onIdle;
  const onPinClickRef = React.useRef(onPinClick);
  onPinClickRef.current = onPinClick;
  const dataRef = React.useRef(data);
  dataRef.current = data;
  const paddingRef = React.useRef(padding);
  paddingRef.current = padding;

  React.useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: MAP_STYLE,
      center: [-3.1, 54.5],
      zoom: 9,
      attributionControl: false,
      interactive,
      canvasContextAttributes: { preserveDrawingBuffer },
      ...(pixelRatio ? { pixelRatio } : {}),
      fadeDuration: 0,
    });
    mapRef.current = map;

    map.on("load", () => {
      // Runways/taxiways read as stray strokes under the share overlays (a
      // gliding-field cross once landed right beneath the stats), and add
      // nothing at route-share zooms - drop them from the basemap.
      for (const layer of map.getStyle().layers ?? []) {
        if ("source-layer" in layer && layer["source-layer"] === "aeroway") {
          map.setLayoutProperty(layer.id, "visibility", "none");
        }
      }
      const sources = buildSources(dataRef.current);
      map.addSource("route", { type: "geojson", data: sources.route as never });
      map.addSource("pins", { type: "geojson", data: sources.pins as never });
      map.addSource("ends", { type: "geojson", data: sources.ends as never });

      map.addLayer({
        id: "route-halo",
        type: "line",
        source: "route",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: { "line-color": "rgba(9,12,16,0.55)", "line-width": 11 },
      });
      map.addLayer({
        id: "route-line",
        type: "line",
        source: "route",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: { "line-color": GOLD, "line-width": 4.5, "line-opacity": 0.95 },
      });
      // Same marker images the explore map registers (gold "collected" set).
      map.addImage("peak-collected", createMarkerImage(GOLD, PEAK_MARKER_PX, PEAK_GLYPH), { pixelRatio: 2 });
      map.addImage("peak-collected-trig", createMarkerImage(GOLD, PEAK_MARKER_PX, PEAK_GLYPH, { trigBadge: true }), {
        pixelRatio: 2,
      });
      for (const [category, glyph] of Object.entries(LANDMARK_GLYPHS)) {
        map.addImage(`lm-${category}-c`, createMarkerImage(GOLD, LANDMARK_MARKER_PX, glyph), { pixelRatio: 2 });
      }
      map.addLayer({
        id: "pins-symbol",
        type: "symbol",
        source: "pins",
        layout: {
          "icon-image": ["get", "icon"],
          "icon-size": 1,
          "icon-allow-overlap": true,
          "icon-anchor": "center",
          // Peaks draw over landmarks when they crowd.
          "symbol-sort-key": ["match", ["get", "kind"], "peak", 0, 1],
        },
      });
      map.addLayer({
        id: "ends-circle",
        type: "circle",
        source: "ends",
        paint: {
          "circle-color": ["match", ["get", "end"], "finish", "#FFFFFF", "#090C10"],
          "circle-radius": 5.5,
          "circle-stroke-width": 2,
          "circle-stroke-color": ["match", ["get", "end"], "finish", "#090C10", "#FFFFFF"],
        },
      });

      if (interactive) {
        map.on("click", "pins-symbol", (e) => {
          const props = e.features?.[0]?.properties as
            | { kind?: string; id?: string; slug?: string; name?: string }
            | undefined;
          if (!props?.id || !props.slug || (props.kind !== "peak" && props.kind !== "landmark")) return;
          onPinClickRef.current?.({ kind: props.kind, id: props.id, slug: props.slug, name: props.name ?? "" });
        });
        map.on("mouseenter", "pins-symbol", () => {
          map.getCanvas().style.cursor = "pointer";
        });
        map.on("mouseleave", "pins-symbol", () => {
          map.getCanvas().style.cursor = "";
        });
      }

      const bbox = routeBbox(dataRef.current);
      if (bbox) {
        map.fitBounds([[bbox[0], bbox[1]], [bbox[2], bbox[3]]], {
          padding: paddingRef.current,
          duration: 0,
          maxZoom: 15,
        });
      }
      map.once("idle", () => onIdleRef.current?.(map));
    });

    return () => {
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    const sources = buildSources(data);
    (map.getSource("route") as maplibregl.GeoJSONSource | undefined)?.setData(sources.route as never);
    (map.getSource("pins") as maplibregl.GeoJSONSource | undefined)?.setData(sources.pins as never);
    (map.getSource("ends") as maplibregl.GeoJSONSource | undefined)?.setData(sources.ends as never);
  }, [data]);

  return <div ref={containerRef} style={{ position: "absolute", inset: 0, ...style }} />;
}
